import type { FeatureCollection, Polygon } from "geojson"

/**
 * Convert latitude (degrees) to Mercator Y (degrees-equivalent scale).
 * Matches the scale of longitude so that 1 unit = 1 degree at the equator.
 */
function mercatorY(lat: number): number {
  const latRad = (lat * Math.PI) / 180
  return Math.log(Math.tan(Math.PI / 4 + latRad / 2)) * (180 / Math.PI)
}

/** Inverse: convert Mercator Y back to latitude (degrees). */
function mercatorYToLat(my: number): number {
  return (
    ((2 * Math.atan(Math.exp((my * Math.PI) / 180)) - Math.PI / 2) * 180) /
    Math.PI
  )
}

/**
 * Generate a flat-top hex grid as GeoJSON covering the given bounding box.
 * Hexagon geometry is computed in Mercator space so cells appear uniform
 * under a Mercator map projection regardless of latitude.
 *
 * @param bbox - [minLng, minLat, maxLng, maxLat]
 * @param cellSize - circumradius of each hexagon in Mercator-degree units
 *                   (same scale as longitude degrees at the equator)
 */
export function generateHexGrid(
  bbox: [number, number, number, number],
  cellSize: number
): FeatureCollection<Polygon> {
  const [minLng, minLat, maxLng, maxLat] = bbox
  const minMY = mercatorY(minLat)
  const maxMY = mercatorY(maxLat)

  const R = cellSize / 2
  const hexH = Math.sqrt(3) * R
  const rowSpacing = (Math.sqrt(3) / 2) * R
  const colSpacing = 3 * R

  const features: FeatureCollection<Polygon>["features"] = []

  for (let row = 0; ; row++) {
    const cy = minMY + row * rowSpacing // Mercator Y
    if (cy - hexH / 2 > maxMY) break

    const offset = row % 2 === 0 ? 0 : 1.5 * R

    for (let col = 0; ; col++) {
      const cx = minLng + col * colSpacing + offset // longitude
      if (cx - R > maxLng) break

      // Compute vertices in Mercator space, convert Y back to latitude
      const ring: [number, number][] = []
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i
        ring.push([
          cx + R * Math.cos(angle),
          mercatorYToLat(cy + R * Math.sin(angle)),
        ])
      }
      ring.push([cx + R, mercatorYToLat(cy)]) // close ring (angle 0)

      features.push({
        type: "Feature",
        properties: {},
        geometry: { type: "Polygon", coordinates: [ring] },
      })
    }
  }

  return { type: "FeatureCollection", features }
}
